import { useEffect, useState } from "react";
import { hasAnyWindow, isOpenAt } from "@toema/shared-types";
import type { HoursProfile, OrderingState } from "@toema/shared-types";
import type { ShopIdentity } from "../lib/shop.js";
import { isPaused } from "../lib/ordering.js";
import PausedPill from "./PausedPill.js";
import StatusPill from "./StatusPill.js";
import "./Hero.css";

interface HeroProps {
  shop: ShopIdentity;
  profile: HoursProfile;
  ordering: OrderingState | null;
}

/** How often the open/closed pill re-checks itself. */
const TICK_MS = 60_000;

/**
 * Splits a name so the mark can set the last word apart, the way the sign
 * does. "Best Kebab Ghazaouet" reads as BEST KEBAB + GHAZAOUET.
 */
function splitMark(name: string): { lead: string; tail: string } {
  const words = name.trim().split(/\s+/);
  if (words.length < 2) return { lead: name, tail: "" };
  return { lead: words.slice(0, -1).join(" "), tail: words[words.length - 1] ?? "" };
}

export default function Hero({ shop, profile, ordering }: HeroProps) {
  const [now, setNow] = useState(() => new Date());

  /* The pill said "Open now" until the page was reloaded, so a page left on a
     counter through closing time went on inviting orders. */
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const open = isOpenAt(profile, now);
  const { lead, tail } = splitMark(shop.name);
  const hasHours = hasAnyWindow(profile);
  const paused = isPaused(ordering);

  return (
    <header className="hero">
      <div className="wrap">
        <p className="hero__eyebrow">Charcoal grill · Pickup &amp; delivery</p>
        <h1 className="hero__mark">
          {lead}
          {tail !== "" && <span>{tail}</span>}
        </h1>
        {shop.tagline !== null && <p className="hero__tag">{shop.tagline}</p>}
        {paused && ordering ? (
          <PausedPill ordering={ordering} timezone={profile.timezone} now={now} className="hero__status" />
        ) : (
          hasHours && (
            <StatusPill tone={open ? "lit" : "shut"} className="hero__status">
              {open
                ? "Open now — taking orders"
                : "Closed — orders come through for next opening"}
            </StatusPill>
          )
        )}
      </div>
    </header>
  );
}
